import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatHint, MatLabel } from '@angular/material/form-field';
import { Store } from '@ngrx/store';
import { isEmpty as _isEmpty } from 'lodash';
import { Subscription } from 'rxjs';

import { IssuesListSettingsMessage } from '@ninety/platform/models/issues/issues-list-settings-message';
import type { IssuesSolveRate } from '@ninety/platform/models/issues/issues-solve-rate';
import {
  CompanyService,
  CompanyActions,
  selectCompany,
  TeamListStateActions,
  selectLanguage,
} from '@ninety/platform/state';
import {
  TerraCheckboxModule,
  TerraDescriptionModule,
  TerraFormFieldModule,
  TerraIconModule,
  TerraNumberInputModule,
} from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';
import { MeetingService } from '@ninety/web/pages/meeting/_shared/services/meeting.service';

interface IssuesListSettingsForm {
  issueVoting?: FormControl<boolean>;
  votesPerUser?: FormControl<number>;
  issueRating: FormControl<boolean>;
  issuesSolveRate?: FormGroup<{ enabled: FormControl<boolean>; value: FormControl<number> }>;
}

export type IssuesListSettingsDialogData = {
  issueVoting: boolean;
  teamId: string;
  issuesSolveRate: IssuesSolveRate;
  isShortTerm: boolean;
};
@Component({
  selector: 'ninety-issue-list-settings-dialog',
  templateUrl: './issues-list-settings-dialog.component.html',
  styleUrls: ['./issues-list-settings-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    ButtonComponent,
    MatDialogClose,
    TerraIconModule,
    FormsModule,
    ReactiveFormsModule,
    TerraCheckboxModule,
    TerraDescriptionModule,
    MatLabel,
    MatHint,
    TerraFormFieldModule,
    TerraNumberInputModule,
    MatDialogActions,
    AsyncPipe,
  ],
})
export class IssueListSettingsDialogComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject<MatDialogRef<IssueListSettingsDialogComponent>>(MatDialogRef);
  private readonly meetingService = inject(MeetingService);
  private readonly companyService = inject(CompanyService);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  data = inject<IssuesListSettingsDialogData>(MAT_DIALOG_DATA);

  form: FormGroup<IssuesListSettingsForm>;
  DEFAULT_ISSUES_SOLVE_RATE = 25;

  private readonly subscriptions = new Subscription();

  private readonly company$ = this.store.select(selectCompany);
  readonly language$ = this.store.select(selectLanguage);

  ngOnInit(): void {
    this.subscriptions.add(
      this.company$.subscribe(company => {
        this.form = this.fb.group({
          issueRating: new FormControl(company?.settings.issueRating),
        });

        const { votingEnabled, votingValue } = {
          votingEnabled: company?.settings.issueVoting,
          votingValue: company?.settings.issueVoting ? this.data.issueVoting : false,
        };

        this.form.addControl('issueVoting', new FormControl({ value: votingValue, disabled: !votingEnabled }));

        if (company?.settings.issueVoting && this.meetingService.currentMeeting?.inProgress && this.data.isShortTerm) {
          this.form.addControl(
            'votesPerUser',
            new FormControl(this.meetingService.currentMeeting.votesPerUser, [
              Validators.required,
              Validators.min(0),
              Validators.max(10),
            ])
          );
        }

        if (this.data.isShortTerm) {
          const { enabled, value } = this.data.issuesSolveRate || {
            enabled: false,
            value: this.DEFAULT_ISSUES_SOLVE_RATE,
          };

          this.form.addControl(
            'issuesSolveRate',
            new FormGroup({
              enabled: new FormControl(enabled),
              value: new FormControl(value, [Validators.required, Validators.min(0), Validators.max(100)]),
            })
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  save() {
    const broadcastDocument: IssuesListSettingsMessage = {};

    if (this.form.controls.votesPerUser?.dirty) {
      broadcastDocument.votesPerUser = this.form.controls.votesPerUser.value;
      this.meetingService.updateCurrentMeeting({ votesPerUser: this.form.controls.votesPerUser.value }).subscribe();
    }

    if (this.form.controls.issueRating.dirty) {
      broadcastDocument.issueRating = this.form.controls.issueRating.value;
      const issueRating = this.form.controls.issueRating.value;
      this.store.dispatch(CompanyActions.updateCompanySetting({ settings: { issueRating } }));
      this.companyService.updateSettingsWithKeyValue('issueRating', issueRating).subscribe();
    }

    if (this.form.controls.issuesSolveRate?.dirty || this.form.controls.issueVoting?.dirty) {
      let issuesSolveRate = null;
      let issueVoting = null;
      let update = {};
      if (this.form.controls.issuesSolveRate?.dirty) {
        broadcastDocument.issuesSolveRate = {
          teamId: this.data.teamId,
          ...this.form.controls.issuesSolveRate.getRawValue(),
        };
        issuesSolveRate = this.form.controls.issuesSolveRate.getRawValue();
        update = { issuesSolveRate };
      }
      if (this.form.controls.issueVoting?.dirty) {
        broadcastDocument.issueVoting = {
          teamId: this.data.teamId,
          value: this.form.controls.issueVoting.value,
        };
        issueVoting = this.form.controls.issueVoting.value;
        update = { ...update, issueVoting };
      }

      this.store.dispatch(TeamListStateActions.updateSettings({ teamId: this.data.teamId, update }));
    }

    if (!_isEmpty(broadcastDocument)) {
      this.meetingService
        .broadcastMessage({
          messageType: 'meeting-issues-settings',
          document: broadcastDocument,
        })
        .subscribe();
    }

    this.dialogRef.close(true);
  }
}
